// pages/Agents/Workflows/WorkflowCreate.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper, Box, Typography, TextField, Button, Divider, Alert,
  CircularProgress, ToggleButtonGroup, ToggleButton,
  FormControl, InputLabel, Select, MenuItem, Accordion, AccordionSummary,
  AccordionDetails, Chip, Card, CardActionArea, CardContent, Collapse,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WorkflowBuilder from "./WorkflowBuilder";
import AIAgentConfig from "./AIAgentConfig";
import WORKFLOW_TEMPLATES, { TEMPLATE_CATEGORIES } from "./workflowTemplates";

export default function WorkflowCreate() {
  const { chatbotId, workflowUid } = useParams();
  const navigate = useNavigate();
  const isEdit = !!workflowUid;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [actions, setActions] = useState([]);

  const [form, setForm] = useState({
    name: "",
    description: "",
    mode: "WORKFLOW",
    active_mode: "WORKFLOW",
    trigger_type: "MANUAL",
    trigger_config: {},
    status: "DRAFT",
    agent_goal: "",
    allowed_actions: [],
    chatbot_id: chatbotId,
  });
  const [steps, setSteps] = useState([]);
  const [showPipelineTemplates, setShowPipelineTemplates] = useState(false);
  const [pipelineTemplateCategory, setPipelineTemplateCategory] = useState("surveys");
  const [selectedPipelineTemplate, setSelectedPipelineTemplate] = useState(null);

  const handleApplyPipelineTemplate = (tpl) => {
    setSteps(tpl.steps.map((s) => ({ ...s })));
    setForm((prev) => ({
      ...prev,
      name: prev.name || tpl.name,
      description: prev.description || tpl.description,
    }));
    setSelectedPipelineTemplate(tpl.id);
    setShowPipelineTemplates(false);
  };

  // Fetch available actions
  useEffect(() => {
    axiosInstance.get("/api/workflows/actions").then((res) => {
      setActions(res.data.data || []);
    }).catch(() => {});
  }, []);

  // Load workflow for editing
  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    axiosInstance.get(`/api/workflows/${workflowUid}`)
      .then((res) => {
        const wf = res.data.data;
        setForm({
          name: wf.name || "",
          description: wf.description || "",
          mode: wf.mode,
          active_mode: wf.active_mode || wf.mode,
          trigger_type: wf.trigger_type,
          trigger_config: wf.trigger_config || {},
          status: wf.status,
          agent_goal: wf.agent_goal || "",
          allowed_actions: wf.allowed_actions || [],
          chatbot_id: wf.chatbot_id,
        });
        setSteps(wf.steps || []);
      })
      .catch(() => setError("Failed to load workflow"))
      .finally(() => setLoading(false));
  }, [workflowUid, isEdit]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validateWorkflow = () => {
    // Basic validations
    if (!chatbotId) {
      return "Chatbot ID is required";
    }
    if (!form.name || !form.name.trim()) {
      return "Workflow name is required";
    }
    if (form.name.trim().length < 3) {
      return "Workflow name must be at least 3 characters";
    }

    // Workflow Pipeline mode validations
    if (form.active_mode === "WORKFLOW") {
      if (!steps || steps.length === 0) {
        return "Workflow Pipeline must have at least one step";
      }

      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        if (!step.action_type) {
          return `Step ${i + 1}: Please select an action`;
        }

        // Check required fields in action_config based on action schema
        const action = actions.find((a) => a.action_type === step.action_type);
        if (action?.schema?.required) {
          for (const reqField of action.schema.required) {
            const value = step.action_config?.[reqField];
            if (value === undefined || value === null || value === "") {
              return `Step ${i + 1} (${action.label}): "${reqField}" is required`;
            }
          }
        }
      }
    }

    // AI Agent mode validations
    if (form.active_mode === "AI_AGENT") {
      if (!form.agent_goal || !form.agent_goal.trim()) {
        return "AI Agent must have a goal defined";
      }
      if (form.agent_goal.trim().length < 10) {
        return "AI Agent goal must be at least 10 characters";
      }
      if (!form.allowed_actions || form.allowed_actions.length === 0) {
        return "AI Agent must have at least one action selected";
      }
    }

    return null; // No errors
  };

  const handleSave = async () => {
    // Run validation
    const validationError = validateWorkflow();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      let currentWorkflowUid = workflowUid;

      if (isEdit) {
        await axiosInstance.put(`/api/workflows/${workflowUid}`, form);
      } else {
        const res = await axiosInstance.post("/api/workflows", form);
        currentWorkflowUid = res.data.data.workflow_uid;
      }

      // Save steps for WORKFLOW mode
      if (form.active_mode === "WORKFLOW" && steps.length > 0) {
        await axiosInstance.post(`/api/workflows/${currentWorkflowUid}/steps`, { steps });
      }

      setSuccess("Workflow saved successfully");
      setTimeout(() => navigate(-1), 1000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save workflow");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1000px", bgcolor: "background.paper" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Back</Button>
        <Typography variant="h5" fontWeight={700} color="textPrimary">
          {isEdit ? "Edit Workflow" : "Create Workflow"}
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Basic Info */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
        <TextField
          label="Workflow Name"
          value={form.name}
          onChange={handleChange("name")}
          required
          fullWidth
        />
        <TextField
          label="Description"
          value={form.description}
          onChange={handleChange("description")}
          multiline
          rows={2}
          fullWidth
        />
      </Box>

      {/* Active Mode Selection */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Active Mode
        <Chip
          label={form.active_mode === "AI_AGENT" ? "AI Agent runs on execution" : "Pipeline runs on execution"}
          size="small"
          color={form.active_mode === "AI_AGENT" ? "secondary" : "primary"}
          sx={{ ml: 2 }}
        />
      </Typography>
      <ToggleButtonGroup
        value={form.active_mode}
        exclusive
        onChange={(e, val) => val && setForm((prev) => ({ ...prev, active_mode: val }))}
        sx={{ mb: 3 }}
      >
        <ToggleButton value="WORKFLOW">
          <AccountTreeIcon sx={{ mr: 1 }} /> Workflow Pipeline
        </ToggleButton>
        <ToggleButton value="AI_AGENT">
          <SmartToyIcon sx={{ mr: 1 }} /> AI Agent
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Trigger Configuration */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 3, mb: 1 }}>Trigger</Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Trigger Type</InputLabel>
          <Select
            value={form.trigger_type}
            label="Trigger Type"
            onChange={handleChange("trigger_type")}
          >
            <MenuItem value="MANUAL">Manual</MenuItem>
            <MenuItem value="SCHEDULED">Scheduled</MenuItem>
            <MenuItem value="EVENT">Event-based</MenuItem>
          </Select>
        </FormControl>

        {form.trigger_type === "SCHEDULED" && (
          <TextField
            label="Interval (seconds)"
            type="number"
            value={form.trigger_config?.interval_seconds || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                trigger_config: { ...prev.trigger_config, interval_seconds: parseInt(e.target.value) || 0 },
              }))
            }
            sx={{ width: 200 }}
          />
        )}

        {form.trigger_type === "EVENT" && (
          <TextField
            label="Event Type"
            placeholder="e.g., volunteer.created"
            value={form.trigger_config?.event_type || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                trigger_config: { ...prev.trigger_config, event_type: e.target.value },
              }))
            }
            sx={{ width: 300 }}
          />
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Both mode configurations — active mode renders first with animation */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          "& > *": {
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          },
        }}
      >
        {[
          ...(form.active_mode === "AI_AGENT" ? ["AI_AGENT", "WORKFLOW"] : ["WORKFLOW", "AI_AGENT"]),
        ].map((mode) =>
          mode === "WORKFLOW" ? (
            <Accordion
              key="WORKFLOW"
              expanded={form.active_mode === "WORKFLOW"}
              onChange={(e, isExpanded) => {
                if (isExpanded) setForm((prev) => ({ ...prev, active_mode: "WORKFLOW" }));
              }}
              sx={{
                border: form.active_mode === "WORKFLOW" ? "2px solid" : "1px solid",
                borderColor: form.active_mode === "WORKFLOW" ? "primary.main" : "divider",
                borderRadius: "8px !important",
                opacity: form.active_mode === "WORKFLOW" ? 1 : 0.7,
                transform: form.active_mode === "WORKFLOW" ? "scale(1)" : "scale(0.98)",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": { opacity: 1, transform: "scale(1)" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AccountTreeIcon color={form.active_mode === "WORKFLOW" ? "primary" : "disabled"} />
                  <Typography fontWeight={600}>Workflow Pipeline</Typography>
                  {form.active_mode === "WORKFLOW" && (
                    <Chip label="ACTIVE" color="primary" size="small" sx={{ ml: 1 }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoFixHighIcon />}
                    endIcon={showPipelineTemplates ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    onClick={() => setShowPipelineTemplates(!showPipelineTemplates)}
                    sx={{ mb: 1.5 }}
                  >
                    {showPipelineTemplates ? "Hide Templates" : "Use a Predefined Template"}
                  </Button>

                  <Collapse in={showPipelineTemplates} timeout={300}>
                    {/* Resource category selector */}
                    <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                      {TEMPLATE_CATEGORIES.map((cat) => (
                        <Chip
                          key={cat.key}
                          label={`${cat.icon} ${cat.label}`}
                          color={pipelineTemplateCategory === cat.key ? "primary" : "default"}
                          variant={pipelineTemplateCategory === cat.key ? "filled" : "outlined"}
                          onClick={() => setPipelineTemplateCategory(cat.key)}
                          clickable
                          sx={{ fontWeight: pipelineTemplateCategory === cat.key ? 700 : 400 }}
                        />
                      ))}
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      {WORKFLOW_TEMPLATES
                        .filter((tpl) => tpl.category === pipelineTemplateCategory)
                        .map((tpl) => (
                        <Card
                          key={tpl.id}
                          variant="outlined"
                          sx={{
                            transition: "all 0.25s ease",
                            border: selectedPipelineTemplate === tpl.id ? "2px solid" : "1px solid",
                            borderColor: selectedPipelineTemplate === tpl.id ? "primary.main" : "divider",
                            transform: selectedPipelineTemplate === tpl.id ? "scale(1.02)" : "scale(1)",
                            "&:hover": {
                              borderColor: "primary.light",
                              transform: "scale(1.02)",
                              boxShadow: 2,
                            },
                          }}
                        >
                          <CardActionArea onClick={() => handleApplyPipelineTemplate(tpl)}>
                            <CardContent sx={{ py: 1.5, px: 2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                <Typography variant="subtitle2" fontWeight={700}>
                                  {tpl.name}
                                </Typography>
                                {selectedPipelineTemplate === tpl.id && (
                                  <Chip label="Applied" color="primary" size="small" />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                {tpl.description}
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {tpl.steps.map((s, i) => (
                                  <Chip
                                    key={i}
                                    label={`${i + 1}. ${s.name}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: 10, height: 20 }}
                                  />
                                ))}
                              </Box>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
                <WorkflowBuilder steps={steps} setSteps={setSteps} actions={actions} chatbotId={chatbotId} />
              </AccordionDetails>
            </Accordion>
          ) : (
            <Accordion
              key="AI_AGENT"
              expanded={form.active_mode === "AI_AGENT"}
              onChange={(e, isExpanded) => {
                if (isExpanded) setForm((prev) => ({ ...prev, active_mode: "AI_AGENT" }));
              }}
              sx={{
                border: form.active_mode === "AI_AGENT" ? "2px solid" : "1px solid",
                borderColor: form.active_mode === "AI_AGENT" ? "secondary.main" : "divider",
                borderRadius: "8px !important",
                opacity: form.active_mode === "AI_AGENT" ? 1 : 0.7,
                transform: form.active_mode === "AI_AGENT" ? "scale(1)" : "scale(0.98)",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": { opacity: 1, transform: "scale(1)" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SmartToyIcon color={form.active_mode === "AI_AGENT" ? "secondary" : "disabled"} />
                  <Typography fontWeight={600}>AI Agent</Typography>
                  {form.active_mode === "AI_AGENT" && (
                    <Chip label="ACTIVE" color="secondary" size="small" sx={{ ml: 1 }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <AIAgentConfig
                  agentGoal={form.agent_goal}
                  setAgentGoal={(val) => setForm((prev) => ({ ...prev, agent_goal: val }))}
                  allowedActions={form.allowed_actions}
                  setAllowedActions={(val) => setForm((prev) => ({ ...prev, allowed_actions: val }))}
                  actions={actions}
                />
              </AccordionDetails>
            </Accordion>
          )
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Save */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Workflow"}
        </Button>
      </Box>
    </Paper>
  );
}
